import { RefObject, useEffect, useRef, useState } from 'react';

const hasDraggedFiles = (dataTransfer: DataTransfer | null): boolean => {
  if (!dataTransfer) {
    return false;
  }

  return dataTransfer.files.length > 0 || Array.from(dataTransfer.types).includes('Files');
};

const isAcceptedFile = (file: File, acceptedFileExtensions?: string[]): boolean => {
  if (!acceptedFileExtensions || acceptedFileExtensions.length === 0) {
    return true;
  }

  const lowerCaseFileName = file.name.toLowerCase();

  return acceptedFileExtensions.some(extension => {
    const normalizedExtension = extension.toLowerCase().startsWith('.')
      ? extension.toLowerCase()
      : `.${extension.toLowerCase()}`;
    return lowerCaseFileName.endsWith(normalizedExtension);
  });
};

export function useFileDragDrop(
  onFileDrop: (file: File | null) => void,
  acceptedFileExtensions?: string[],
  targetRef?: RefObject<HTMLElement | null>,
) {
  const dragDepthRef = useRef(0);
  const acceptedFileExtensionsRef = useRef(acceptedFileExtensions);
  const [isDragging, setIsDragging] = useState(false);
  const onFileDropRef = useRef(onFileDrop);

  useEffect(() => {
    onFileDropRef.current = onFileDrop;
  }, [onFileDrop]);

  useEffect(() => {
    acceptedFileExtensionsRef.current = acceptedFileExtensions;
  }, [acceptedFileExtensions]);

  useEffect(() => {
    const target = targetRef?.current ?? document.body;

    const handleDragEnter = (event: DragEvent) => {
      if (!hasDraggedFiles(event.dataTransfer)) {
        return;
      }

      event.preventDefault();
      dragDepthRef.current += 1;
      setIsDragging(true);
    };

    const handleDragOver = (event: DragEvent) => {
      if (!hasDraggedFiles(event.dataTransfer)) {
        return;
      }

      event.preventDefault();
      setIsDragging(true);
    };

    const handleDragLeave = (event: DragEvent) => {
      event.preventDefault();

      dragDepthRef.current = Math.max(0, dragDepthRef.current - 1);

      if (dragDepthRef.current === 0) {
        setIsDragging(false);
      }
    };

    const handleDrop = (event: DragEvent) => {
      event.preventDefault();
      dragDepthRef.current = 0;
      setIsDragging(false);

      const droppedFile = event.dataTransfer?.files?.[0];

      if (!droppedFile || !isAcceptedFile(droppedFile, acceptedFileExtensionsRef.current)) {
        return;
      }

      onFileDropRef.current(droppedFile);
    };

    target.addEventListener('dragenter', handleDragEnter);
    target.addEventListener('dragover', handleDragOver);
    target.addEventListener('dragleave', handleDragLeave);
    target.addEventListener('drop', handleDrop);

    return () => {
      target.removeEventListener('dragenter', handleDragEnter);
      target.removeEventListener('dragover', handleDragOver);
      target.removeEventListener('dragleave', handleDragLeave);
      target.removeEventListener('drop', handleDrop);
    };
  }, [targetRef]);

  return { isDragging };
}
